import { FC } from 'react';

import { Ad } from '@adflow/ad';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box } from '@chakra-ui/react';

import type { EditorCanvasSession } from './useEditorCanvasSession';

export type EditorCanvasStageProps = {
  session: EditorCanvasSession;
  frame: { width: number; height: number };
  stageSize: { width: number; height: number };
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  testId?: string;
  flex?: number;
};

export const EditorCanvasStage: FC<EditorCanvasStageProps> = ({
  session,
  frame,
  stageSize,
  animationOverrides = null,
  testId = 'editor-canvas-stage',
  flex,
}) => {
  const {
    stageRef,
    viewport,
    displayArtwork,
    selectionBounds,
    clipToFrame,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  } = session;

  return (
    <Box
      ref={stageRef}
      data-test-id={testId}
      position="relative"
      flex={flex}
      minH={flex != null ? 0 : undefined}
      width={flex != null ? '100%' : `${stageSize.width}px`}
      height={flex != null ? '100%' : `${stageSize.height}px`}
      overflow="hidden"
      bg="gray.100"
      sx={{ touchAction: 'none', userSelect: 'none' }}
      onDragStart={(event) => event.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <Box
        position="absolute"
        left={`${viewport.panX}px`}
        top={`${viewport.panY}px`}
        transform={`scale(${viewport.zoom})`}
        transformOrigin="0 0"
      >
        <Ad
          adElements={displayArtwork}
          clipToFrame={clipToFrame}
          isPreview
          animationOverrides={animationOverrides}
        />
        <Box
          position="absolute"
          left={0}
          top={0}
          width={`${frame.width}px`}
          height={`${frame.height}px`}
          border="1px dashed"
          borderColor="gray.400"
          pointerEvents="none"
        />
        {selectionBounds ? (
          <Box
            position="absolute"
            left={`${selectionBounds.x}px`}
            top={`${selectionBounds.y}px`}
            width={`${selectionBounds.width}px`}
            height={`${selectionBounds.height}px`}
            border="2px solid"
            borderColor="blue.500"
            pointerEvents="none"
          />
        ) : null}
      </Box>
    </Box>
  );
};

EditorCanvasStage.displayName = 'EditorCanvasStage';
